import { Component, OnInit, Input, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { ImmediatelyErrorStateMatcher } from '../../components/immediately-error-state-matcher.component';
import { SelectBox } from '../../models/selectBox.model';
import { ZyuushoDataApi } from '../../models/zyuushoDataApi';
import { ZyuushoInfo } from '../../models/zyuushoInfo.model';
import { UsedSzza001aService } from '../../services/used-szza001a.service';
import { Szza001aResponse, ResponseCommon } from 'sg-api';

/**
 * 住所検索共通
 * プレゼンテーショナルコンポーネント内で住所の入力が必要な場合に、本コンポーネントを取り込む。
 * @export
 * @class ZyuushoSearchApiComponent
 * @implements {OnInit}
 * @implements {OnChanges}
 */
@Component({
  selector: 'app-zyuusho-search-api',
  templateUrl: './zyuusho-search-api.component.html',
  styleUrls: ['./zyuusho-search-api.component.scss']
})
export class ZyuushoSearchApiComponent implements OnInit, OnDestroy {
  /** 入力：取り込み先のFormGroup */
  @Input() parentFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  /** 入力：追加Form名（APIに合わせた名前を設定する） */
  @Input() zyuushoInfo: ZyuushoInfo = {
    addrcd: { formControlName: 'visitAddrcd', labelName: '住所選択リスト', required: false },
    postno: { formControlName: 'visitPostno', labelName: '郵便番号', required: false },
    banti: { formControlName: 'visitPosvisitBantitno', labelName: '番地', required: false },
    gou: { formControlName: 'visitGou', labelName: '号', required: false },
    supadrKananm: { formControlName: 'visitSupadrKananm', labelName: '住宅・棟号カナ名', required: false },
    yagouKananm: { formControlName: 'visitYagouKananm', labelName: '屋号・業種カナ名', required: false },
  };
  /** ドロップダウンを制御するためのコントロール */
  @ViewChild('selectZyuushoCode', { static: false }) selectZyuushoCode;
  /** コントロールが無効な状態となったタイミングで即座にエラーを検知するための定義 */
  matcher: ImmediatelyErrorStateMatcher = new ImmediatelyErrorStateMatcher();
  /** 住所コード検索エラーメッセージ */
  errorMessage: string = '';
  /** 住所情報取得結果 */
	response: Szza001aResponse;
	/** responseCommon */
	responseCommon: ResponseCommon;
  /** 住所リスト */
  addrcdList: SelectBox[] = [];
  /** 住所コード検索用Form */
  searchFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  /** 住所コード */
  addrcd: UntypedFormControl = new UntypedFormControl({ value: '', disabled: true });
  /** 郵便番号（FormGroupには追加しない） */
  postno: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 番地 */
  banti: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 号 */
  gou: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 住宅・棟号カナ名 */
  supadrKananm: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 屋号・業種カナ名 */
  yagouKananm: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });


  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {FormBuilder} fb
   * @param {SZZA001AService} sZZA001AService
   * @param {ElementRef} el
   * @memberof ZyuushoComponent
   */
  constructor(private fb: UntypedFormBuilder, private el: ElementRef, private usedSzza001aService: UsedSzza001aService) {
  }

  /**
   * 画面起動時処理
   * @memberof ZyuushoComponent
   */
  ngOnInit(): void {
    // 親コンポーネントのFormGroupに、住所のFormGroupを追加
    this.parentFormGroup.addControl(this.zyuushoInfo.addrcd.formControlName, this.addrcd);
    this.parentFormGroup.addControl(this.zyuushoInfo.banti.formControlName, this.banti);
    this.parentFormGroup.addControl(this.zyuushoInfo.gou.formControlName, this.gou);
    this.parentFormGroup.addControl(this.zyuushoInfo.supadrKananm.formControlName, this.supadrKananm);
    this.parentFormGroup.addControl(this.zyuushoInfo.yagouKananm.formControlName, this.yagouKananm);

    // 住所コード検索用Formに設定
    this.searchFormGroup.addControl(this.zyuushoInfo.postno.formControlName, this.postno);

    // 必須設定
    Object.keys(this.zyuushoInfo).forEach(key => {
      if (this.zyuushoInfo[key].required == true) {
        if (key == 'postno') {
          // 郵便番号は、親Formにセットしないため、個別に設定
          this.postno.setValidators(Validators.required);
          return;
        }
        this.parentFormGroup.controls[this.zyuushoInfo[key].formControlName].setValidators(Validators.required);
        this.parentFormGroup.controls[this.zyuushoInfo[key].formControlName].updateValueAndValidity();
      }
    });
  }

  /**
   * 終了処理
   * @memberof ZyuushoSearchApiComponent
   */
  ngOnDestroy(): void {
    this.parentFormGroup.removeControl(this.zyuushoInfo.addrcd.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.banti.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.gou.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.supadrKananm.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.yagouKananm.formControlName);
  }

  /**
   * 画面項目に、親コンポーネントから渡された初期値を設定
   * @param {ZyuushoData} ZyuushoDataApi
   * @memberof ZyuushoComponent
   */
  public setInitalValue(zyuushoDataApi: ZyuushoDataApi): void {
    this.parentFormGroup.controls[this.zyuushoInfo.banti.formControlName].setValue(zyuushoDataApi.banti);
    this.parentFormGroup.controls[this.zyuushoInfo.gou.formControlName].setValue(zyuushoDataApi.gou);
    this.parentFormGroup.controls[this.zyuushoInfo.supadrKananm.formControlName].setValue(zyuushoDataApi.supadrKananm);
    this.parentFormGroup.controls[this.zyuushoInfo.yagouKananm.formControlName].setValue(zyuushoDataApi.yagouKananm);
    this.searchFormGroup.controls[this.zyuushoInfo.postno.formControlName].setValue(zyuushoDataApi.postno);

    // 住所コードリスト
    this.searchZyuushoCode(this.searchFormGroup.controls[this.zyuushoInfo.postno.formControlName].value, () => {
      // 住所コードリストから現在の住所コードを選択
      this.parentFormGroup.controls[this.zyuushoInfo.addrcd.formControlName].setValue(zyuushoDataApi.addrcd);
    });
  }

  /**
   * 検索ボタン押下時処理
   * @param {*} event
   * @memberof ZyuushoComponent
   */
  onClickZyuushoSearch(): void {
    // 郵便番号から住所コードを取得し、住所リストに表示
    this.searchZyuushoCode(this.searchFormGroup.controls[this.zyuushoInfo.postno.formControlName].value, () => {
      // 住所選択プルダウンをオープン
      this.selectZyuushoCode.open();
    });
  }

  /**
   * 住所情報取得を実行
   * @param {string} zipCode
   * @param {() => void} callback
   * @memberof ZyuushoComponent
   */
  searchZyuushoCode(zipCode: string, callback: () => void): void {
    this.errorMessage = '';

    // APIを実行し住所を取得
    this.usedSzza001aService.searchZyuushoCode(zipCode).subscribe(response => {
      this.response = response;
      this.responseCommon = response.responseCommon;
      let addList: SelectBox[] = [];
      if (response.addressSearchInfoToZipCodeDtoList != null) {
        for (let dto of response.addressSearchInfoToZipCodeDtoList) {
          let editedValue: string = '';
          if(dto.prefecturesName) editedValue = dto.prefecturesName;
          if(dto.cityName) editedValue += dto.cityName;
          if(dto.townAreaName) editedValue += dto.townAreaName;
          if(dto.ooazaName) editedValue += dto.ooazaName;
          let data: SelectBox = {
            value: dto.addressCode,
            viewValue: editedValue
          }
          addList.push(data);
        }
        this.addrcdList = addList;
        this.parentFormGroup.controls[this.zyuushoInfo.addrcd.formControlName].enable();

        callback();
      }
    },
      error => {
        this.errorMessage = '住所の取得に失敗しました'
      }
    );
  }

}
